import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
} from "@mui/material";
import { useContent } from "../context/ContentContext";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";

export default function ContactPage() {
  const content = useContent();
  const company = content.getCompany();

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // "success" | "error" | null

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = e.currentTarget; // the <form> element
    const formData = new FormData(form);

    const payload = {
      name: formData.get("name"),
      email: formData.get("email"),
      message: `
Phone: ${formData.get("phone")}
Company: ${formData.get("company") || "N/A"}

${formData.get("message")}
      `.trim(),
    };

    setLoading(true);
    setStatus(null);

    try {
      const res = await fetch("https://sln-backend.sridhar-gkrishnamurthy.workers.dev/message", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to send message");
      }

      setStatus("success");
      form.reset(); // ✅ Reset form after successful submission
    } catch (err) {
      console.error(err);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ px: 2, py: 6 }}>
      {/* Heading */}
      <Typography variant="h4" sx={{ mb: 2, fontWeight: "bold" }} gutterBottom>
        We'd Love to Hear From You
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
        Whether you're curious about our <strong>products and services</strong>,{" "}
        looking for a <strong>demo</strong>, or just want to connect — we're ready
        to answer any and all questions.
      </Typography>

      <Grid container spacing={4}>
        {/* Left: Contact Form */}
        <Grid item xs={12} md={6}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              bgcolor: "#fafafa",
              p: 4,
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField name="name" label="Name*" fullWidth required variant="standard" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField name="phone" label="Phone*" fullWidth required variant="standard" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="email"
                  label="Email*"
                  fullWidth
                  required
                  type="email"
                  variant="standard"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField name="company" label="Company" fullWidth variant="standard" />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="message"
                  label="Message"
                  fullWidth
                  required
                  multiline
                  rows={4}
                  variant="standard"
                />
              </Grid>
            </Grid>

            {/* Status messages */}
            {status === "success" && (
              <Alert severity="success" sx={{ mt: 3 }}>
                Message sent successfully!
              </Alert>
            )}
            {status === "error" && (
              <Alert severity="error" sx={{ mt: 3 }}>
                Failed to send message. Please try again.
              </Alert>
            )}

            <Box sx={{ mt: 4 }}>
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                sx={{
                  borderRadius: "32px",
                  px: 4,
                  py: 1.5,
                  fontWeight: "bold",
                  background: "linear-gradient(to right, #9c27b0, #3f51b5)",
                  textTransform: "none",
                  fontSize: "16px",
                }}
              >
                {loading ? "Sending..." : "Submit!"}
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* Right: Contact Info */}
        <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Paper elevation={1} sx={{ display: "flex", alignItems: "center", p: 2 }}>
            <MailOutlineIcon sx={{ fontSize: 32, mr: 2 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Mail To Us
              </Typography>
              <Typography variant="body2" color="secondary.main">
                {company.email}
              </Typography>
            </Box>
          </Paper>

          <Paper elevation={1} sx={{ display: "flex", alignItems: "center", p: 2 }}>
            <PhoneIcon sx={{ fontSize: 32, mr: 2 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Connect With Us
              </Typography>
              <Typography variant="body2" color="secondary.main">
                {company.phones?.[0]}
              </Typography>
            </Box>
          </Paper>

          <Paper elevation={1} sx={{ display: "flex", alignItems: "center", p: 2 }}>
            <LocationOnIcon sx={{ fontSize: 32, mr: 2 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Visit Us
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {company.address || "52, Gollahalli Tulaslipura Main Road, Bengaluru"}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
